import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';

const DrawTool = forwardRef(({ svgWidth, svgHeight }, ref) => {
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [mousePosition, setMousePosition] = useState(null);
  const [paths, setPaths] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [base64Image, setBase64Image] = useState(''); // 存储Base64图片数据

  const IMAGE_SIZE = 2500;
  const MAX_SVG_WIDTH = 1660;
  const MAX_SVG_HEIGHT = 1110;
  const MAX_OFFSET_X = IMAGE_SIZE - MAX_SVG_WIDTH;
  const MAX_OFFSET_Y = IMAGE_SIZE - MAX_SVG_HEIGHT;

  // 加载图片并转换为 Base64
  useEffect(() => {
    const loadImageAsBase64 = () => {
      const img = new Image();
      img.src = '/poster.png'; // 确保这个图片在public目录下
      img.crossOrigin = 'anonymous'; // 如果图片是外部资源，需要设置跨域
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png'); // 将图片转换为 Base64
        setBase64Image(dataURL);
      };
    };

    loadImageAsBase64();
  }, []);

  const getMousePosition = (event) => {
    const svg = event.target.closest('svg');
    if (!svg) return { x: 0, y: 0 };
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const cursorPoint = point.matrixTransform(svg.getScreenCTM().inverse());
    return { x: cursorPoint.x, y: cursorPoint.y };
  };

  const limitImagePosition = (position) => {
    return {
      x: Math.max(Math.min(position.x, 0), -MAX_OFFSET_X),
      y: Math.max(Math.min(position.y, 0), -MAX_OFFSET_Y),
    };
  };

  const handleMouseDownOnImage = (event) => {
    setIsDraggingImage(true);
    const { x, y } = getMousePosition(event);
    setMousePosition({ x, y });
  };

  const handleMouseMoveOnImage = (event) => {
    if (isDraggingImage) {
      const { x, y } = getMousePosition(event);
      setImagePosition((prevPosition) => {
        const newPosition = {
          x: prevPosition.x + (x - mousePosition.x),
          y: prevPosition.y + (y - mousePosition.y),
        };
        return limitImagePosition(newPosition);
      });
      setMousePosition({ x, y });
    }
  };

  const handleMouseUpOnImage = () => {
    setIsDraggingImage(false);
    setMousePosition(null);
  };

  const handleMouseDownForDrawing = (event) => {
    const { x, y } = getMousePosition(event);
    setCurrentPath(`M ${x},${y}`);
  };

  const handleMouseMoveForDrawing = (event) => {
    if (currentPath) {
      const { x, y } = getMousePosition(event);
      setCurrentPath((prevPath) => `${prevPath} L ${x},${y}`);
    }
  };

  const handleMouseUpForDrawing = () => {
    if (currentPath) {
      setPaths((prevPaths) => [...prevPaths, currentPath]);
      setCurrentPath('');
    }
  };

  useImperativeHandle(ref, () => ({
    clearPaths: () => {
      setPaths([]);
    },
    downloadSVG: () => {
      const svgElement = document.getElementById('new-draw-svg');
      console.log("SVG element:", svgElement);
      if (!svgElement) {
        console.warn("SVG element not found!");
        return;
      }
  
      console.log("SVG element found:", svgElement);  // 确保它输出
  
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svgElement);
      const blob = new Blob([svgString], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'draw-tool-svg.svg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  }));

  return (
    <svg
      id="new-draw-svg"
      width={svgWidth}
      height={svgHeight}
      style={{ backgroundColor: 'lightgray', cursor: 'pointer' }}
      onMouseDown={handleMouseDownForDrawing}
      onMouseMove={handleMouseMoveForDrawing}
      onMouseUp={handleMouseUpForDrawing}
    >
      <image
        href={base64Image} // 使用 Base64 格式的图片
        x={imagePosition.x}
        y={imagePosition.y}
        width="2500"
        height="2500"
        onMouseDown={handleMouseDownOnImage}
        onMouseMove={handleMouseMoveOnImage}
        onMouseUp={handleMouseUpOnImage}
      />
    <g style={{ opacity: 1,mixBlendMode: 'passthrough' }}>
      {paths.map((path, index) => (
        <path
          key={index}
          d={path}
          stroke="#DEDEDE"
          strokeOpacity={0.9}
          fill="none"
          strokeWidth="120"
          strokeLinecap="round"
          style={{ mixBlendMode: 'difference' }}
        />
      ))}

      {currentPath && (
        <path
          d={currentPath}
          stroke="#ffffff"
          fill="none"
          strokeWidth="120"
          strokeLinecap="round"
          style={{ mixBlendMode: 'difference' }}
        />
      )}
    </g>
    </svg>
  );
});

export default DrawTool;
